import { uuid } from "~/services/uuid";
import { createApp, defineAsyncComponent, defineComponent, getCurrentInstance, h, onMounted, ref, shallowRef } from "vue";

import Modal from "@/components/modal/Modal.vue";

import type { App, AsyncComponentLoader, Component, ComponentPublicInstance, Ref, ShallowRef } from "vue";

import "./polyfill";

export interface UseModal {
	createInstance: (binding: Component, attrs: ModalProps & Record<string, unknown>) => void;
	showModal: <T>(el?: AsyncComponentLoader<new () => T>, props?: Record<string, unknown>) => void;
	closeModal: () => void;
	openModal: Ref<boolean>;
	openModals: Ref<ComponentPublicInstance | null>;
}

export interface ModalProps {
	header?: string;
	outside?: boolean;
	size?: "sm" | "md" | "lg" | "full";
}

type DynamicDialog = [Component, () => void, () => void, Ref<boolean>, Ref<ComponentPublicInstance | null>];

const modals = shallowRef<App<Element>[]>([]);
const dialogContainers = shallowRef<HTMLDialogElement[]>([]);
const dialogInstances = shallowRef<ComponentPublicInstance[]>([]);
const dialogUuids = shallowRef<string[]>([]);
const openModal = ref<boolean>(false);
const openModals = ref<ComponentPublicInstance | null>(null);

function openDialog(): void {
	const dialogContainer = dialogContainers.value.pop();
	if (dialogContainer && document.body.contains(dialogContainer)) {
		dialogContainer.showModal();
		document.body.style.overflow = "hidden"; // Disable background scrolling

		openModal.value = true;
	} else {
		console.error("Dialog container is not in the document.");
	}
}

export function closeDialog(): void {
	const dialogContainer = dialogContainers.value.pop();
	if (dialogContainer) {
		dialogContainer.close();
		document.body.style.overflow = ""; // Re-enable background scrolling

		setTimeout(() => dialogContainer.remove(), 100);

		modals.value.pop();
		dialogInstances.value.pop();
		dialogUuids.value.pop();

		openModal.value = dialogContainers.value.length > 0;
		if (dialogInstances.value.length > 0) {
			openModals.value = dialogInstances.value[dialogInstances.value.length - 1];
		} else {
			openModals.value = null;
		}
	}
}

export function dialog<T>(el: AsyncComponentLoader<new () => T>): DynamicDialog {
	const component = defineAsyncComponent(el);

	const { showModal, closeModal, createInstance, openModal, openModals } = useModal();

	const elementUuid = ref<string>("");

	const wrapper = defineComponent({
		setup() {
			const instance = getCurrentInstance();
			const attrs = instance ? instance.attrs : {};

			onMounted(() => {
				elementUuid.value = uuid.value;
				createInstance(component, attrs);
			});

			return () => h("div", { id: elementUuid.value });
		}
	});

	return [wrapper, showModal, closeModal, openModal, openModals];
}

export function useModal(): UseModal {
	const { initModal, createInstance } = setupModal();

	function showModal<T>(el?: AsyncComponentLoader<new () => T>, props?: Record<string, unknown>): void {
		if (el) {
			const component = defineAsyncComponent(el);
			createInstance(component, props || {});
		}

		initModal();
		openDialog();
		openModals.value = dialogInstances.value[dialogInstances.value.length - 1];
	}

	function closeModal(): void {
		closeDialog();
	}

	return {
		createInstance,
		showModal,
		closeModal,
		openModal,
		openModals
	};
}

function appendDialog(bindAttrs: ShallowRef<(ModalProps & Record<string, unknown>) | null>): HTMLDialogElement {
	const id = uuid.value;

	const dialogContainer = document.createElement("dialog");
	dialogContainer.id = id;
	dialogContainer.classList.add("__innosend-dialog");

	if (bindAttrs.value?.size) {
		dialogContainer.classList.add(`dialog-${bindAttrs.value.size}`);
	}

	document.body.appendChild(dialogContainer);
	dialogContainers.value.push(dialogContainer);
	return dialogContainer;
}

function setupModal() {
	const bindContainer = shallowRef<Component | null>(null);
	const bindAttrs = shallowRef<(ModalProps & Record<string, unknown>) | null>(null);

	function initModal(): void {
		const dialogContainer = appendDialog(bindAttrs);

		if (bindAttrs.value?.outside !== false) {
			dialogContainer.addEventListener("click", closeDialog);
		}

		const modalProps: Record<string, unknown> = { ...bindAttrs.value };

		const modal = createApp({
			render: () => h(Modal, modalProps, { default: () => (bindContainer.value ? h(bindContainer.value, modalProps) : null) })
		});

		modals.value.push(modal);
		dialogInstances.value.push(modal.mount(dialogContainer));
		dialogContainers.value.push(dialogContainer);

		const handleKeydown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				closeDialog();
			}
		};

		document.addEventListener("keydown", handleKeydown);

		dialogContainer.addEventListener("close", () => document.removeEventListener("keydown", handleKeydown));
	}

	async function createInstance(binding: Component, attrs: ModalProps & Record<string, unknown>): Promise<void> {
		bindContainer.value = binding;
		bindAttrs.value = attrs;
	}

	return {
		initModal,
		createInstance
	};
}
